import { useState, useEffect, useCallback, useRef } from 'react'
import { notificationsApi } from '../api/client'

const POLL_INTERVAL = 30_000 // 30 seconds

export function useNotifications() {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef(null)

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await notificationsApi.unreadCount()
      setUnreadCount(data.unread_count)
    } catch {
      // Silently ignore – best-effort polling
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await notificationsApi.list({ limit: 30 })
      setNotifications(data)
      // Sync unread count from the returned data
      setUnreadCount(data.filter((n) => !n.is_read).length)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [])

  const markRead = useCallback(async (id) => {
    try {
      await notificationsApi.markRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
      )
      setUnreadCount((c) => Math.max(0, c - 1))
    } catch {
      // ignore
    }
  }, [])

  const markAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
      setUnreadCount(0)
    } catch {
      // ignore
    }
  }, [])

  // Poll for unread count on mount, then every POLL_INTERVAL
  useEffect(() => {
    fetchUnreadCount()
    intervalRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL)
    return () => clearInterval(intervalRef.current)
  }, [fetchUnreadCount])

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markRead,
    markAllRead,
  }
}
